import { request } from '@/lib/api'

/**
 * Резюме переговоров и расшифровки звонков (тикет #130).
 *
 * Поля ответа сравниваются нестрого (`== null`): сервер не кладёт в JSON пустые поля вовсе
 * (`non_null`), и `undefined` здесь — обычное «не заполнено».
 */

export type SummarySubject = 'deal' | 'counterparty' | 'call'

/** Запись, из которой взято утверждение, под её меткой в запросе к модели. */
export interface SummarySource {
  label: string
  /** Вид события ленты: `email`, `call`, `message`, `comment`, `meeting`, `note`, `task`. */
  kind: string
  recordType: string
  recordId: string
  at?: string | null
  title?: string | null
  direction?: string | null
  /** У звонка: слышала ли модель его слова (была ли расшифровка). */
  transcribed?: boolean | null
  /** У фрагмента резюме звонка: с какой миллисекунды записи он начинается. */
  offsetMs?: number | null
  dealId?: string | null
}

export interface SummaryStatement {
  text: string
  sources: SummarySource[]
  /** Метки, которые модель назвала, но которых в запросе не было. */
  unknownSources?: string[] | null
}

export interface UnsupportedStatement {
  section: SummarySection
  text: string
  /** `no_source` — модель не сослалась ни на что; `unknown_source` — сослалась на выдуманное. */
  reason: 'no_source' | 'unknown_source'
  citedLabels?: string[] | null
}

export type SummarySection = 'agreements' | 'openQuestions' | 'nextSteps'

export const SUMMARY_SECTIONS: SummarySection[] = ['agreements', 'openQuestions', 'nextSteps']

export interface ConversationSummary {
  taskId: string
  kind: string
  entityType: SummarySubject
  entityId: string
  /** `working`, `retrying` (провайдер не ответил, повтор назначен), `failed` (отказ) или `ready`. */
  state: 'working' | 'retrying' | 'failed' | 'ready'
  message?: string | null
  requestedAt?: string | null
  completedAt?: string | null
  sourceCount: number
  /** Сколько записей периода не поместилось в запрос. */
  omittedSources: number
  sections?: Partial<Record<SummarySection, SummaryStatement[]>> | null
  unsupported?: UnsupportedStatement[] | null
  sources?: SummarySource[] | null
}

export interface SummaryRequest {
  entityType: SummarySubject
  entityId: string
  from?: string
  to?: string
}

export function requestSummary(body: SummaryRequest): Promise<ConversationSummary> {
  return request<ConversationSummary>('/ai/summaries', { method: 'POST', body })
}

export function fetchSummary(taskId: string, signal?: AbortSignal): Promise<ConversationSummary> {
  return request<ConversationSummary>(`/ai/summaries/${taskId}`, { signal })
}

/** Последнее своё резюме записи; `undefined` — его ещё не заказывали (204). */
export function fetchLatestSummary(
  entityType: SummarySubject,
  entityId: string,
  signal?: AbortSignal,
): Promise<ConversationSummary | undefined> {
  const query = new URLSearchParams({ entityType, entityId })
  return request<ConversationSummary | undefined>(`/ai/summaries?${query.toString()}`, { signal })
}

export interface TranscriptSegment {
  channel: string
  startMs: number
  endMs: number
  text: string
}

export interface CallTranscript {
  activityId: string
  /** `none` — не заказывали; `pending`/`submitted` — в работе; `failed` — повтор назначен; `rejected` — отказ. */
  status: 'none' | 'pending' | 'submitted' | 'succeeded' | 'failed' | 'rejected'
  recorded: boolean
  transcript?: string | null
  segments?: TranscriptSegment[] | null
  error?: string | null
  retryAfter?: string | null
  requestedAt?: string | null
  completedAt?: string | null
}

export function fetchTranscript(activityId: string, signal?: AbortSignal): Promise<CallTranscript> {
  return request<CallTranscript>(`/telephony/activities/${activityId}/transcript`, { signal })
}

export function requestTranscript(activityId: string): Promise<CallTranscript> {
  return request<CallTranscript>(`/telephony/activities/${activityId}/transcript`, { method: 'POST', body: {} })
}

/** Подключение SpeechKit (экран администратора ИИ). */
export interface SpeechKitSettings {
  enabled: boolean
  endpoint?: string | null
  operationEndpoint?: string | null
  folderId?: string | null
  model?: string | null
  apiKeySet: boolean
  usable: boolean
  problem?: string | null
}

export interface SpeechKitUpdate {
  enabled: boolean
  endpoint?: string
  operationEndpoint?: string
  folderId?: string
  model?: string
  /** Нет поля — ключ прежний; пустая строка — стереть. */
  apiKey?: string
}

export function fetchSpeechKit(signal?: AbortSignal): Promise<SpeechKitSettings> {
  return request<SpeechKitSettings>('/admin/integrations/speechkit', { signal })
}

export function saveSpeechKit(body: SpeechKitUpdate): Promise<SpeechKitSettings> {
  return request<SpeechKitSettings>('/admin/integrations/speechkit', { method: 'PUT', body })
}
